import {
  Image,
  StyleSheet,
  Text,
  View,
  Alert,
  Linking,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import React from 'react';
import { useState } from 'react';
import GoogleSignInButton from '../../Components/GoogleSignInButton';
import ScreenWrapper from '../../Components/ScreenWrapper';
import { theme } from '../../constants/theme';
import { hp, wp } from '../../helpers/common';
import {NativeStackNavigationProp} from '@react-navigation/native-stack';
import {AuthStackNavigationParms} from './AuthenticationStack';
import { useAuth } from '../../Utils/AuthProvider';
import googleAuthService from '../../services/googleAuthService';
import iconImage from '../../../assets/images/icon.png';
import welcomeImage from '../../../assets/images/welcome.jpg';

type Props = {
  navigation: NativeStackNavigationProp<AuthStackNavigationParms, 'Welcome'>;
};

const WelcomePage: React.FC<Props> = ({navigation}) => {
  const { setAuth } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      const result = await googleAuthService.signIn();
      
      if (result.success) {
        // Google Sign-In successful
        // The AuthProvider will automatically handle the session
      } else {
        Alert.alert('Error', result.error || 'Google Sign-In failed');
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      Alert.alert('Error', 'Google Sign-In failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handlePrivacyPolicy = () => {
    Linking.openURL('https://dynamicdivas.in/privacy.html');
  };
  const handleTermsOfUse = () => {
    Linking.openURL('https://dynamicdivas.in/terms.html');
  };

  return (    
    <ScreenWrapper bg={theme.colors.background}> 
        <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
          {/* Header Section */}
          <View style={styles.headerSection}>
            <View style={styles.logoContainer}>
              <View style={styles.logoCircle}>
                <Image 
                  source={iconImage}
                  style={styles.logoImage}
                  resizeMode="contain"
                />
              </View>
            </View>
            
            <View style={styles.titleSection}>
              <Text style={styles.title}>Dynamic Divas</Text>
              <Text style={styles.subtitle}>Inspire to rise</Text>
            </View>
          </View>

          {/* Hero Image Section */}
          <View style={styles.imageSection}>
            <View style={styles.imageContainer}>
              <Image
                source={welcomeImage}
                style={styles.welcomeImage} 
                resizeMode='cover'
              />
              <View style={styles.imageOverlay} />
            </View>
          </View>

          {/* Content Section */}
          <View style={styles.contentSection}>
            <Text style={styles.description}>
              The fastest-growing community for women — support, connection, empowerment.
            </Text>
            
            <View style={styles.featuresContainer}>
              <View style={styles.featureItem}>
                <View style={[styles.featureIcon, { backgroundColor: theme.colors.accent1 }]}>
                  <Text style={styles.featureIconText}>💪</Text>
                </View>
                <Text style={styles.featureText}>Empowerment</Text>
              </View>
              
              <View style={styles.featureItem}>
                <View style={[styles.featureIcon, { backgroundColor: theme.colors.accent2 }]}>
                  <Text style={styles.featureIconText}>🤝</Text>
                </View>
                <Text style={styles.featureText}>Support</Text>
              </View>
              
              <View style={styles.featureItem}>
                <View style={[styles.featureIcon, { backgroundColor: theme.colors.accent3 }]}>
                  <Text style={styles.featureIconText}>🌟</Text>
                </View>
                <Text style={styles.featureText}>Connection</Text>
              </View>
            </View>
          </View>

          {/* Sign In Section */}
          <View style={styles.signInSection}>
            <GoogleSignInButton
              onPress={handleGoogleSignIn}
              loading={googleLoading}
              title="Continue with Google"
            />
            
            <View style={styles.termsContainer}>
              <Text style={styles.termsText}>
                By continuing, you agree to our{' '}
                <Text style={styles.linkText} onPress={handleTermsOfUse}>
                  Terms of Service
                </Text>
                {' '}and{' '}
                <Text style={styles.linkText} onPress={handlePrivacyPolicy}>
                  Privacy Policy
                </Text>
              </Text>
            </View>
          </View>
        </ScrollView>      
    </ScreenWrapper>    
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.background,
    paddingHorizontal: theme.spacing.lg,
  },
  headerSection: {
    alignItems: 'center',
    paddingTop: hp(4),
    paddingBottom: hp(4),
  },
  logoContainer: {
    marginBottom: theme.spacing.md,
  },
  logoCircle: {
    width: wp(20),
    height: wp(20),
    borderRadius: wp(10),
    backgroundColor: 'white',
    alignItems: 'center',
    justifyContent: 'center',
    ...theme.shadows.medium,
  },
  logoImage: {
    width: wp(12),
    height: wp(12),
  },
  titleSection: {
    alignItems: 'center',
  },
  title: {
    color: theme.colors.textDark,
    fontSize: hp(4.5),
    textAlign: 'center',
    fontWeight: '800',
    marginBottom: theme.spacing.xs,
  },
  subtitle: {
    color: theme.colors.primary,
    fontSize: hp(2),
    textAlign: 'center',
    fontWeight: '600',
  },
  imageSection: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageContainer: {
    width: wp(80),
    height: hp(25),
    borderRadius: theme.radius.lg,
    overflow: 'hidden',
    ...theme.shadows.large,
  },
  welcomeImage: {
    width: '100%',
    height: '100%',
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
  },
  contentSection: {
    paddingVertical: theme.spacing.lg,
  },
  description: {
    textAlign: 'center',
    fontSize: hp(1.8),
    color: theme.colors.textLight,
    lineHeight: hp(2.5),
    marginBottom: theme.spacing.lg,
    paddingHorizontal: theme.spacing.md,
  },
  featuresContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: theme.spacing.md,
  },
  featureItem: {
    alignItems: 'center',
    flex: 1,
  },
  featureIcon: {
    width: wp(12),
    height: wp(12),
    borderRadius: wp(6),
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.sm,
    ...theme.shadows.small,
  },
  featureIconText: {
    fontSize: hp(2.5),
  },
  featureText: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
    fontWeight: '500',
    textAlign: 'center',
  },
  signInSection: {
    paddingBottom: hp(6),
    alignItems: 'center',
  },
  termsContainer: {
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.xl,
    paddingHorizontal: theme.spacing.lg,
  },
  termsText: {
    textAlign: 'center',
    color: theme.colors.textLight,
    fontSize: hp(1.3),
    lineHeight: hp(1.8),
  },
  linkText: {
    color: theme.colors.primary,
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
});

export default WelcomePage;
